import { useEffect, useRef, useState } from 'react'
import { fetchPetAvatarsBatch } from '../../utils/petPhotoUtils'

/**
 * Batch-loads pet avatar URLs for the Bookings lists.
 *
 * Booking rows arrive from the appointments query, which carries `pet_id` but
 * no photo — the avatar lives in `pet_photos`. Fetching per row would be a
 * query per visit, so this collects the ids and resolves them in one call
 * (fetchPetAvatarsBatch), the same way ClientsManager does.
 *
 * Ids already looked up are never re-requested, so paginating "load more" only
 * fetches the newly appended pets. Pets without an avatar simply stay absent
 * from the map and fall back to BkAvatar's paw placeholder.
 *
 * @param {string[]} petIds - pet ids in the current list (may repeat/be empty)
 * @returns {Object} map of petId -> avatar URL
 */
export function usePetAvatars(petIds) {
	const [avatars, setAvatars] = useState({})
	const requested = useRef(new Set())

	// Depend on the joined ids rather than the array identity — the caller
	// rebuilds the list on every render.
	const key = (petIds || []).filter(Boolean).join(',')

	useEffect(() => {
		const missing = [...new Set(key ? key.split(',') : [])]
			.filter(id => !requested.current.has(id))
		if (missing.length === 0) return

		missing.forEach(id => requested.current.add(id))
		fetchPetAvatarsBatch(missing).then(map => {
			if (map && Object.keys(map).length > 0) {
				setAvatars(prev => ({ ...prev, ...map }))
			}
		})
	}, [key])

	return avatars
}

export default usePetAvatars
